import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SliderSection } from '../components/SliderSection';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { setSiteMeta } from '../utils/meta';

export const LandingPage: React.FC = () => {
  const [matric, setMatric] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Report problem form state
  const [reportName, setReportName] = useState('');
  const [reportEmail, setReportEmail] = useState('');
  const [reportMessage, setReportMessage] = useState('');
  const [reportSuccess, setReportSuccess] = useState(false);

  const { loginVoter } = useAuth();
  const [electionInfo, setElectionInfo] = useState(() => api.getElectionSettingsSync());

  useEffect(() => {
    if (electionInfo) {
      setSiteMeta({ election: electionInfo });
    }
    api.getElectionInfo().then((info) => {
      if (info) {
        setElectionInfo(info);
        setSiteMeta({ election: info });
      }
    });
  }, []);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage('');

    // Safely extract input values directly from DOM form elements to ensure
    // browser autofill, password managers (e.g. Chrome/Keychain), and normal typing are captured
    const form = e.currentTarget;
    const matricVal = (
      (form.elements.namedItem('login-matric') as HTMLInputElement)?.value ||
      matric ||
      ''
    ).trim();
    const passwordVal = (
      (form.elements.namedItem('login-password') as HTMLInputElement)?.value ||
      password ||
      ''
    ).trim();

    if (!matricVal || !passwordVal) {
      setErrorMessage('Please enter both your matric number and password.');
      return;
    }

    setLoading(true);

    try {
      const res = await api.loginVoter(matricVal, passwordVal);
      if (res.success && res.voter && res.token) {
        loginVoter(res.voter, res.token);
        navigate('/vote');
      } else {
        if (res.code === 'VOTING_NOT_STARTED') {
          navigate('/error/votingnotstarted');
          return;
        }
        if (res.code === 'VOTING_ENDED') {
          navigate('/error/votingended');
          return;
        }
        if (res.code === 'YOU_HAVE_VOTED') {
          navigate('/error/youhavevoted');
          return;
        }
        setErrorMessage(res.message || 'Invalid matric number or password. Please try again.');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Error logging in.');
    } finally {
      setLoading(false);
    }
  };

  const handleReportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await api.submitReport(reportName, reportEmail, reportMessage);
    setReportSuccess(true);
    setReportName('');
    setReportEmail('');
    setReportMessage('');
  };

  return (
    <>
      <div className="eqd-main">
        {/* First Section */}
        <div className="eqd-left">
          {/* Header */}
          <Header />

          {/* Cast Your Vote */}
          <div className="eqdl-main">
            <div className="main-texts">
              <div className="main-header">
                <h2>{electionInfo.title} is live!</h2>
              </div>
              <div className="main-paragraph">
                <p>Enter your login details to begin voting.</p>
              </div>
            </div>
            <div className="loginForm">
              <div className="eac-form">
                <form id="loginVoteForm" onSubmit={handleLogin}>
                  <div className="full-input">
                    <label htmlFor="login-email">Matric Number</label>
                    <input
                      id="login-email"
                      name="login-matric"
                      type="text"
                      placeholder="Enter your matric number"
                      value={matric}
                      onChange={(e) => setMatric(e.target.value)}
                      onInput={(e) => setMatric((e.target as HTMLInputElement).value)}
                      autoComplete="username"
                      required
                    />
                  </div>
                  <div className="full-input">
                    <label htmlFor="login-password">Password</label>
                    <div className="password-toggle">
                      <input
                        id="login-password"
                        name="login-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
                        autoComplete="current-password"
                        required
                      />
                      <span
                        className={`toggle-icon2 ${showPassword ? 'visible' : ''}`}
                        onClick={() => setShowPassword(!showPassword)}
                        role="button"
                        tabIndex={0}
                        title={showPassword ? 'Hide password' : 'Show password'}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setShowPassword(!showPassword);
                          }
                        }}
                      ></span>
                    </div>
                  </div>
                  {errorMessage && (
                    <div className="invalid-credentials" style={{ display: 'block' }}>
                      <p>*{errorMessage}</p>
                    </div>
                  )}
                  <button type="submit" className="eac-submit" id="eac-submit" disabled={loading}>
                    <p>{loading ? 'Authenticating...' : 'Login to Vote'}</p>
                  </button>
                </form>
              </div>
            </div>
            <div className="logincomplaints">
              <p>Issues logging in? <a href="#rapf">Send us a message</a></p>
            </div>
          </div>

          {/* Footer (Desktop) */}
          <div className="dt-foot"></div>
        </div>

        {/* Second Section */}
        <div className="eqd-right">
          {/* Sliders */}
          <SliderSection />

          {/* Footer (Mobile) */}
          <div className="mb-foot"></div>
        </div>
      </div>

      {/* Who Can Vote? */}
      <div className="who-can-vote">
        <div className="wcv-img">
          <img src="/assets/images/wcv-img.png" alt="Black man raising hands" />
        </div>
        <div className="wcv-texts">
          <div className="wcvt">
            <h2>Of Course You Have The Right To Vote...</h2>
            <p>
              But only accredited members of {electionInfo.organizationName || 'the society'} are eligible to vote for this election. If you think this is a mistake, please <a href="#rapf">send us a message</a> to resolve this.
            </p>
          </div>
        </div>
      </div>

            {/* The Process (Restored from Election Setup Legacy) */}
      <div className="voting-process">
        <div className="home-vp">
          <div className="vp-process">
            <img src="/assets/images/material-symbols_line-start-circle.svg" alt="Step 1" />
            <div className="vpp">
              <h2>Begin</h2>
              <p>Begin the process by inputting your matric number and password in the appropriate fields and hitting the login button.</p>
            </div>
          </div>
          <div className="vp-process">
            <img src="/assets/images/material-symbols_line-mid-circle.svg" alt="Step 2" />
            <div className="vpp">
              <h2>Verify</h2>
              <p>Verify that the information displayed on the page is yours and tallies with your personal information. If not, contact support.</p>
            </div>
          </div>
          <div className="vp-process">
            <img src="/assets/images/material-symbols_line-end-circle.svg" alt="Step 3" />
            <div className="vpp">
              <h2>Vote</h2>
              <p>Remember, your vote matters. Choose wisely, but don't overthink it. Somewhere inside us resides the power to change the world.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Report A Problem */}
      <div className="report-a-problem">
        <div className="rap-form">
          <form id="rapf" onSubmit={handleReportSubmit}>
            <div className="rapf-head">
              <h2>Report A Problem</h2>
              <p>Have any issues with the site? You can report the problem to us. We'll be sure to get back to you.</p>
            </div>
            {reportSuccess ? (
              <div style={{ padding: '20px', backgroundColor: '#ECFDF5', border: '1px solid #10B981', borderRadius: '4px', textAlign: 'center', color: '#065F46', fontWeight: 600, marginBottom: '20px' }}>
                Thank you! Your ticket has been dispatched to the electoral support desk.
              </div>
            ) : (
              <>
                <div className="rapf-inputs">
                  <input
                    type="text"
                    id="report-name"
                    placeholder="Your Name"
                    value={reportName}
                    onChange={(e) => setReportName(e.target.value)}
                    required
                    autoComplete="name"
                  />
                  <input
                    type="email"
                    id="report-email"
                    placeholder="Your Email"
                    value={reportEmail}
                    onChange={(e) => setReportEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>
                <textarea
                  name="report-message"
                  id="report-message"
                  cols={30}
                  rows={10}
                  placeholder="Message"
                  value={reportMessage}
                  onChange={(e) => setReportMessage(e.target.value)}
                  required
                ></textarea>
                <div className="rapf-cta">
                  <button type="submit" id="rapf-submit">
                    <p>Send Message</p>
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </>
  );
};
